import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Search, Loader2, Plus, X, Globe } from 'lucide-react';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useAuth } from '@/hooks/useAuth';
import { createClient } from '@/services/firestoreService';
import { useAppStore } from '@/store/appStore';

interface SearchResultItem {
    id: string;
    name: string;
    country?: string;
    label?: string;
}

interface AddClientModalProps {
    isOpen: boolean;
    onClose: () => void;
    countryStructureId: string;
    countrySapId: string;
    onClientAdded: (client: { id: string, name: string }) => void;
}

export function AddClientModal({ isOpen, onClose, countryStructureId, countrySapId, onClientAdded }: AddClientModalProps) {
    const { user } = useAuth();
    useAppStore();

    // Estados
    const [searchTerm, setSearchTerm] = useState('');
    const [sapResults, setSapResults] = useState<SearchResultItem[]>([]);
    const [isSearchingSAP, setIsSearchingSAP] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [addError, setAddError] = useState<string | null>(null);

    const inputRef = useRef<HTMLInputElement>(null);

    const appId = typeof window !== 'undefined' && (window as any).__app_id ? (window as any).__app_id : 'default-app-id';

    // Reset state when opening
    useEffect(() => {
        if (isOpen) {
            setSearchTerm('');
            setSapResults([]);
            setIsSearchingSAP(false);
            setAddError(null);
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    // Lógica Firestore: Escribir búsqueda
    useEffect(() => {
        if (!isOpen || !user?.uid || !appId || searchTerm.length < 3) return;

        const handler = setTimeout(async () => {
            setIsSearchingSAP(true);
            const isId = /^\d+$/.test(searchTerm);

            try {
                const searchDocRef = doc(db, 'artifacts', appId, 'users', user.uid, 'frontEnd', 'customerSearch');
                await setDoc(searchDocRef, {
                    countryCode: countrySapId || "",
                    kunnr: isId ? searchTerm : "",
                    name1: isId ? "" : searchTerm.toUpperCase(),
                    searchTerm: searchTerm,
                    timestamp: new Date().toISOString()
                }, { merge: true });
            } catch (error) {
                console.error("Error escribiendo búsqueda:", error);
                setIsSearchingSAP(false);
            }

        }, 600);

        return () => clearTimeout(handler);
    }, [searchTerm, user, appId, countrySapId, isOpen]);

    // Lógica Firestore: Leer resultados
    useEffect(() => {
        if (!isOpen || !user?.uid || !appId) return;

        const searchDocRef = doc(db, 'artifacts', appId, 'users', user.uid, 'frontEnd', 'customerSearch');
        const unsubscribe = onSnapshot(searchDocRef, (docSnap) => {
            const data = docSnap.data();
            if (data && data.searchResults) {
                const resultsArray = Object.values(data.searchResults) as SearchResultItem[];
                setSapResults(resultsArray);
                setIsSearchingSAP(false);
            }
        });

        return () => unsubscribe();
    }, [user, appId, isOpen]);

    const handleAddClient = async (item: SearchResultItem) => {
        setIsAdding(true);
        setAddError(null);
        try {
            await createClient(countryStructureId, {
                id: item.id,
                name: item.name,
                plant: ''
            });
            onClientAdded({ id: item.id, name: item.name });
            onClose();
        } catch (error: any) {
            console.error("Error adding client:", error);
            setAddError(error.message || "Failed to add client. Please try again.");
        } finally {
            setIsAdding(false);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[80vh]">

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Plus className="w-5 h-5 text-[#eb4a4a]" />
                        Add New Client
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-500 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Search Input */}
                <div className="p-4 border-b border-gray-100">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            ref={inputRef}
                            type="text"
                            className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#eb4a4a] focus:border-transparent transition-all"
                            placeholder="Search by name or SAP ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {isSearchingSAP && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                <Loader2 className="w-4 h-4 animate-spin text-[#eb4a4a]" />
                            </div>
                        )}
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                        Search for a client in SAP to add it to the local database.
                    </p>
                    {addError && (
                        <div className="mt-3 p-2 bg-red-50 border border-red-100 rounded-md flex items-center gap-2 text-red-600 text-xs">
                            <span className="font-bold">Error:</span> {addError}
                        </div>
                    )}
                </div>

                {/* Results List */}
                <div className="overflow-y-auto flex-1 p-2 bg-gray-50/50">
                    {sapResults.length === 0 && !isSearchingSAP && searchTerm.length >= 3 && (
                        <div className="text-center py-8 text-gray-400">
                            <p>No clients found in SAP.</p>
                        </div>
                    )}

                    {sapResults.length === 0 && searchTerm.length < 3 && (
                        <div className="text-center py-8 text-gray-400">
                            <p>Type at least 3 characters to search.</p>
                        </div>
                    )}

                    <ul className="space-y-2">
                        {sapResults.map((item) => (
                            <li key={item.id} className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all flex justify-between items-center group">
                                <div>
                                    <div className="font-medium text-gray-900">{item.name}</div>
                                    <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                        <Globe className="w-3 h-3" /> SAP ID: {item.id}
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleAddClient(item)}
                                    disabled={isAdding}
                                    className="px-3 py-1.5 text-xs font-medium text-white bg-[#eb4a4a] rounded-md hover:bg-[#d93a3a] disabled:opacity-50 transition-colors flex items-center gap-1 opacity-0 group-hover:opacity-100 focus:opacity-100"
                                >
                                    {isAdding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                                    Add
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Footer */}
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900">
                        Cancel
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
